/**
 * /signup while Cubby is closed to new schools: a coming soon page with a
 * waitlist, same idea as Kanvo's app/signup/page.tsx.
 *
 * Differences from Kanvo's version, on purpose:
 * - No "first 100 get 60 days free" offer or spots counter. Cubby has no
 *   pricing yet, so there is nothing true to promise.
 * - The feature list describes what is being built, not what exists.
 * - The form posts through api() to POST /api/waitlist, which verifies
 *   Turnstile in production and stores the entry in app.waitlist. Duplicate
 *   emails get the same success response, so the form cannot be used to check
 *   who is already on the list.
 *
 * When sign-up opens, the real form replaces this file. Nothing else links to
 * the waitlist endpoint.
 */

import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../auth/SessionProvider";
import { Logo } from "../components/Logo";
import { Turnstile } from "../components/Turnstile";
import { Field, inputClass, primaryButtonClass } from "../components/form";
import { ApiError } from "../lib/api";

const BUILDING: { title: string; body: string; marker: string }[] = [
  {
    title: "Attendance and ratios",
    body: "Check children in and out by classroom, with live staff-to-child ratios for every room.",
    marker: "bg-clay",
  },
  {
    title: "Staff time clock",
    body: "Staff clock in to a classroom, so ratios and payroll hours come from the same record.",
    marker: "bg-sun",
  },
  {
    title: "Tuition and billing",
    body: "Recurring tuition, one-off charges and online payments for families.",
    marker: "bg-sky",
  },
  {
    title: "Enrollment and forms",
    body: "Applications, medical forms and permission slips signed online and kept with each child's record.",
    marker: "bg-sage",
  },
  {
    title: "Family messaging",
    body: "Message one family, one classroom or the whole school from the same inbox.",
    marker: "bg-blush",
  },
  {
    title: "Daily reports",
    body: "Meals, naps, diapers and activities logged through the day and sent home at pickup.",
    marker: "bg-leaf-600",
  },
];

export function Signup() {
  const { api, config } = useSession();

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [schoolName, setSchoolName] = useState("");
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [widgetKey, setWidgetKey] = useState(0);
  const [status, setStatus] = useState<"idle" | "sending" | "done">("idle");
  const [error, setError] = useState<string | null>(null);

  const needsCaptcha = config.turnstileSiteKey !== null;
  const waitingOnCaptcha = needsCaptcha && !captchaToken;
  const sending = status === "sending";
  const incomplete = !fullName.trim() || !email.trim() || !schoolName.trim();

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (waitingOnCaptcha) {
      setError("Complete the security check below and try again.");
      return;
    }

    setStatus("sending");
    try {
      await api<void>("/api/waitlist", {
        method: "POST",
        body: JSON.stringify({
          fullName: fullName.trim(),
          email: email.trim(),
          schoolName: schoolName.trim(),
          captchaToken,
        }),
      });
      setStatus("done");
    } catch (caught: unknown) {
      setStatus("idle");
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not reach Cubby. Check your connection and try again.",
      );
      // Tokens are single use; get a fresh widget for the retry.
      setCaptchaToken(null);
      setWidgetKey((key) => key + 1);
    }
  }

  return (
    <div className="min-h-screen bg-cream text-ink">
      <header className="sticky top-0 z-10 border-b border-stone-200/70 bg-cream/90 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-6">
          <Logo />
          <Link
            to="/login"
            className="rounded-xl px-3 py-2 text-sm font-medium text-stone-600 transition-colors hover:bg-stone-200/60 hover:text-ink"
          >
            Sign in
          </Link>
        </div>
      </header>

      <main>
        <section className="mx-auto max-w-2xl px-6 pt-20 text-center sm:pt-28">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-leaf-700">
            In development
          </p>
          <h1 className="mt-4 font-display text-4xl font-semibold leading-[1.1] tracking-tight sm:text-6xl">
            Cubby is almost ready.
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-stone-600 sm:text-lg">
            Preschool software for check-ins, classroom ratios, billing and family updates. We're
            building it alongside one preschool first, then opening it to other schools.
          </p>
          <p className="mx-auto mt-7 max-w-md rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm text-stone-600">
            Cubby is not open to new schools yet. Join the waitlist and we'll email you when it is.
          </p>
        </section>

        <section className="flex justify-center px-6 pb-20 pt-10">
          <div className="w-full max-w-md rounded-3xl border border-stone-200 bg-white p-7 shadow-[0_24px_48px_-24px_rgba(29,35,32,0.22)] sm:p-9">
            {status === "done" ? (
              <div className="py-4 text-center">
                <h2 className="font-display text-2xl font-semibold">You're on the list.</h2>
                <p className="mt-3 text-sm leading-relaxed text-stone-600">
                  We'll email <span className="font-medium text-ink">{email.trim()}</span> when Cubby
                  opens to new schools.
                </p>
              </div>
            ) : (
              <>
                <h2 className="text-center font-display text-2xl font-semibold">Join the waitlist</h2>
                <p className="mt-2 text-center text-sm text-stone-500">
                  One email when we open. Nothing else.
                </p>

                {error ? (
                  <p role="alert" className="mt-6 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-800">
                    {error}
                  </p>
                ) : null}

                <form onSubmit={handleSubmit} className="mt-7 space-y-4">
                  <Field label="Your name" htmlFor="wl-name">
                    <input
                      id="wl-name"
                      type="text"
                      autoComplete="name"
                      required
                      maxLength={120}
                      disabled={sending}
                      value={fullName}
                      onChange={(event) => setFullName(event.target.value)}
                      className={inputClass}
                    />
                  </Field>

                  <Field label="Work email" htmlFor="wl-email">
                    <input
                      id="wl-email"
                      type="email"
                      autoComplete="email"
                      required
                      maxLength={254}
                      disabled={sending}
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      className={inputClass}
                    />
                  </Field>

                  <Field label="School name" htmlFor="wl-school">
                    <input
                      id="wl-school"
                      type="text"
                      autoComplete="organization"
                      required
                      maxLength={160}
                      disabled={sending}
                      value={schoolName}
                      onChange={(event) => setSchoolName(event.target.value)}
                      className={inputClass}
                    />
                  </Field>

                  <div className="pt-1">
                    <button
                      type="submit"
                      disabled={sending || incomplete || waitingOnCaptcha}
                      className={primaryButtonClass}
                    >
                      {sending ? "Joining" : "Join the waitlist"}
                    </button>
                  </div>

                  <Turnstile
                    key={widgetKey}
                    siteKey={config.turnstileSiteKey}
                    onToken={setCaptchaToken}
                  />
                </form>
              </>
            )}
          </div>
        </section>

        <div className="mx-auto flex max-w-5xl items-center gap-4 px-6 pb-10">
          <span className="h-px flex-1 bg-stone-200" />
          <span className="text-xs font-semibold uppercase tracking-[0.14em] text-stone-400">
            What we're building
          </span>
          <span className="h-px flex-1 bg-stone-200" />
        </div>

        <section className="mx-auto grid max-w-5xl gap-4 px-6 pb-20 sm:grid-cols-2 lg:grid-cols-3">
          {BUILDING.map((item) => (
            <div key={item.title} className="rounded-2xl border border-stone-200 bg-white p-6">
              <span aria-hidden="true" className={`block h-1.5 w-8 rounded-full ${item.marker}`} />
              <h3 className="mt-4 text-[15px] font-semibold text-ink">{item.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-stone-600">{item.body}</p>
            </div>
          ))}
        </section>
      </main>

      <footer className="px-6 pb-12 text-center text-sm text-stone-500">
        Already have an account?{" "}
        <Link to="/login" className="font-medium text-leaf-700 hover:text-leaf-800">
          Sign in
        </Link>
      </footer>
    </div>
  );
}
